import { useInkind } from '@gik/api/inkinds/inkind';
import { useWishlistProducts } from '@gik/api/inkinds/wishlistProducts';
import { SkeletonNetworkError, SkeletonNetworkRetryMessage } from '@gik/core/components/NetworkError/NetworkError';
import { useInkindCan } from '@gik/core/store/permissions';
import bemBlock from '@gik/core/utils/bemBlock';
import withComponentErrorBoundary from '@gik/core/utils/withComponentErrorBoundary';
import { Grid } from '@gik/ui-legacy/Grid';
import { ProductTileToolbar } from '@gik/ui-legacy/gik/ProductTile';
import { FrameBone, ParagraphBone } from '@gik/ui-legacy/SkeletonLoader';
import { WishlistSidebarSection } from '@gik/wishlist-page/components/WishlistSidebarSection';
import type React from 'react';
import * as NetworkStoreApi from '@gik/core/stores/NetworkStore.ts';

import SeeFullWishlistButton from './SeeFullWishlistButton';
import { WishlistEmptyState } from './WishlistEmptyState';
import { WishlistProductTile } from './WishlistProductTile';

export interface IWishlistGridProps {
  inkindRouteId: string;
  isFullWishlistPage?: boolean;
  isAmazonVisible?: string;
  skeletonItems?: number;
  cols?: number;
}

const skelBem = bemBlock(`wishlist-grid-skeleton`);
const Skeleton = ({
  skeletonItems = 4,
  onReload,
  showRetrying,
  showError,
}: Partial<IWishlistGridProps> & {
  onReload?: () => void;
  showRetrying?: boolean;
  showError?: boolean;
}) => (
  <div className="relative">
    <Grid className={`${skelBem()} gik-grid--4-cols`}>
      {Array.from(Array(skeletonItems).keys()).map(index => {
        return (
          <div key={index} className={skelBem('item')}>
            <FrameBone className={skelBem('image')} />
            <main>
              <ParagraphBone words={18} className={skelBem('text')} />
            </main>
          </div>
        );
      })}
    </Grid>
    {showRetrying && !showError && <SkeletonNetworkRetryMessage />}
    {showError && <SkeletonNetworkError onReload={onReload} />}
  </div>
);

function WishlistGridComp({
  inkindRouteId,
  isFullWishlistPage,
  isAmazonVisible,
  cols,
}: IWishlistGridProps): React.ReactElement {
  const bem = bemBlock('wishlist-grid');
  const { data: inkindPage, error: inkindPageError } = useInkind(inkindRouteId);
  const canEdit = useInkindCan('manage', inkindRouteId, inkindPage?.groupId);
  const maxProductsToDisplay = isFullWishlistPage ? undefined : 24;

  const requestKey = `wishlistProducts-${inkindRouteId}`;
  const {
    data: items,
    error: wishListProductsError,
    isValidating: isLoadingWishlistProducts,
    mutate,
  } = useWishlistProducts(
    inkindRouteId,
    {},
    {
      requestKey,
    }
  );

  // Check NetworkStore status for retry/error UI
  const isNetworkError = NetworkStoreApi.isError(requestKey);
  const shouldShowRetrying = NetworkStoreApi.shouldShowRetryMessage(requestKey);

  const isLoading = !items || !inkindPage;

  // Show skeleton with retry/error messages while loading or on error.
  // ponytail: gate on data presence, not NetworkStore success — a late-mounting hook joins an
  // already-resolved deduped SWR request, so onSuccess never fires for this requestKey and
  // isSuccess() stays false forever (same race as GIK-11057). NetworkStore only drives error UI.
  if (!items || !inkindPage) {
    return (
      <Skeleton
        showRetrying={shouldShowRetrying}
        showError={isNetworkError}
        onReload={() => {
          NetworkStoreApi.reload(requestKey);
          mutate();
        }}
      />
    );
  }

  const hasItems = items?.length > 0;

  return (
    <WishlistSidebarSection isFullWishlistPage={isFullWishlistPage} inkindRouteId={inkindRouteId} className={bem()}>
      <Grid className={bem(null, null)} cols={cols}>
        {items?.slice(0, maxProductsToDisplay)?.map(item => (
          <WishlistProductTile
            key={item.id}
            item={item}
            inkindRouteId={inkindRouteId}
            toolbar={
              <ProductTileToolbar
                inkindRouteId={inkindRouteId}
                itemId={item.id}
                hasRemoveFromCurrentPageButton={canEdit}
              />
            }
          />
        ))}
      </Grid>
      {!isLoadingWishlistProducts && !hasItems && <WishlistEmptyState inkindRouteId={inkindRouteId} proportion={0.8} />}
      <div className={bem('grid-footer')}>
        {maxProductsToDisplay && maxProductsToDisplay < items?.length && (
          <SeeFullWishlistButton inkindPage={inkindPage} />
        )}
      </div>
    </WishlistSidebarSection>
  );
}

export const WishlistGrid = withComponentErrorBoundary(WishlistGridComp);
